import { useEffect, useRef } from 'react';
import { FILTER_URL_KEYS, filtersFromQuery, hasActiveFilters, withoutFilters } from '../../utils/search-filter-state';
import { useProduct, useUI } from '../../contexts';
import { useSearch } from '../../contexts/SearchProvider';
import { isBackNavigation } from '../../utils/navigation-direction';
import { acquireAdInsertHold } from '../ad-insert-batch';
import { parseListingSort } from '../../utils/listing-sort';
import { recallBrowseList, rememberBrowseList } from '../../utils/browse-list-cache';
import type { Product } from '../../utils/product/product-types';
import type { SearchParams } from './types';

interface UseBrowseListingsOptions {
  /** Server-rendered list for this page. Empty on mobile/Capacitor (no SSR). */
  initialList: Product[];
  /** Active category-group id, or subcategory id when `isSubcategory`. */
  effectiveId: number;
  isSubcategory: boolean;
  currentCategoryId?: number;
  currentCategoryGroupId?: number;
  /** Router query minus the dynamic `category`/`subcategory` segment. */
  urlParams: Record<string, string | string[] | undefined>;
  setPage: (page: number) => void;
  setDone: (done: boolean) => void;
  /** Home page (`/`): categoryGroupId 0, home-listings endpoint, no filters. */
  isHome?: boolean;
  /**
   * The router has read the URL. Until it has, `urlParams` is empty: a reload
   * of `/?sortBy=price_asc` fetched the unsorted feed, then the sorted one, and
   * whichever answered last was shown. Nothing is fetched before this is true.
   */
  ready?: boolean;
}

/**
 * Single owner of browse-listing population for the category / subcategory page.
 *
 * Replaces the former tangle of `useCategoryInit` + `useSearchSync` + the
 * `getProps` effect, all of which wrote `currentList` and fought each other (and
 * the generic SSR-sync effect in ProductProvider) — the source of the
 * stale-listings and infinite-loop bugs.
 *
 * Sources of truth, after this hook:
 *  - **Category identity** is derived from the URL (props seed the first paint).
 *  - **The list is always fetched here.** Browse and home pages send no server
 *    list since 2026-10-12 (pages/browse/[category]/index.tsx), so
 *    `initialList` is empty and every navigation, Back included, fetches what
 *    the URL asks for. One fetch, one writer.
 *  - The `hasServerList` branches below are for a caller that does pass a
 *    server list: there a forward navigation takes it via ProductProvider's
 *    SSR-sync effect, and Back is left to `useScrollRestoration`, whose page
 *    count the grid's pager (search-listing/useGridFeed) fills in order.
 *
 * The effect is keyed on a stable identity string (not the per-render
 * `urlParams` object), so it runs once per real navigation — never every render.
 */
export const useBrowseListings = ({
  initialList,
  effectiveId,
  isSubcategory,
  currentCategoryId,
  currentCategoryGroupId,
  urlParams,
  setPage,
  setDone,
  isHome = false,
  ready = true,
}: UseBrowseListingsOptions) => {
  const { searchParams, setSearchParams, setCurrentCategoryGroupId, getFreshList } = useSearch();
  const { currentList, replaceCurrentList } = useProduct();
  const { searchViewMode, searchViewModeSettled } = useUI();

  const q = typeof urlParams.q === 'string' ? urlParams.q : Array.isArray(urlParams.q) ? urlParams.q[0] : '';
  const sortBy = parseListingSort(urlParams.sortBy);

  /**
   * Home fetches its grid list only for a reader who will see the grid.
   *
   * Showcase readers get their rows from useHomeShowcases, and the grid list was
   * worse than wasted on them: SearchListing grouped it by subcategory into rows
   * of one to five listings, painted those, then replaced every one when the
   * real rows landed -- the "rows with not enough products, switched right
   * after" readers saw. So home waits until the saved view is known (it is read
   * after this hook's first effect; see UIProvider), and fetches only for grid.
   * A switch to grid later fetches then. If the showcase fetch fails and the
   * page falls back to the grid, SearchListing fetches the list itself.
   *
   * A picked sort no longer forces the grid: showcase rows carry it (2026-10-14).
   */
  const homeShowsShowcase = isHome && (searchViewModeSettled === false || searchViewMode === 'showcase');
  /** The sort home's grid was last fetched for; `null` until it has been fetched. */
  const homeFetchedSortRef = useRef<string | null>(null);
  /** Bumped per fetch; an answer that is not the latest is dropped. */
  const fetchSeqRef = useRef(0);
  /** The category whose list this hook last fetched; `null` before the first fetch. */
  const shownCategoryRef = useRef<string | null>(null);
  /**
   * The page identity whose list the shared list now holds: set when this
   * hook's fetch lands or a kept list is put back. Until then the shared list
   * is someone else's (the last page's, or the product page's), and must not be
   * kept under this page's name.
   */
  const listKeyRef = useRef<string | null>(null);

  // Read-latest refs so the effect can depend only on the stable identity key.
  const searchParamsRef = useRef(searchParams);
  const urlParamsRef = useRef(urlParams);
  const initialListRef = useRef(initialList);

  // Sync the read-latest refs after every render (matching the prior
  // every-render assignment). Declared before the main effect so it commits
  // first, guaranteeing the main effect reads the freshest values.
  useEffect(() => {
    searchParamsRef.current = searchParams;
    urlParamsRef.current = urlParams;
    initialListRef.current = initialList;
  });

  // Stable serialization of every URL search param (q + filters: price, condition,
  // sort, …) so a filter-only change still produces a new identity and refetches.
  // The page is ISR, so a filter change does NOT re-run its server list; this
  // key re-fires the client fetch on web and mobile alike.
  const paramsKey = Object.keys(urlParams)
    .sort()
    .map((k) => {
      const v = urlParams[k];
      return `${k}=${Array.isArray(v) ? v.join(',') : v ?? ''}`;
    })
    .join('&');
  const identityKey = !ready
    ? 'not-ready'
    : isHome
      ? `home:${homeShowsShowcase ? 'showcase' : 'grid'}:${sortBy ?? ''}`
      : `${isSubcategory ? 'c' : 'g'}:${effectiveId}:${paramsKey}`;

  // Keep what this page shows, load-more pages included, so Back can put it
  // back. Declared before the main effect: in the commit that changes the
  // page, this must still see the old key and skip, not file the old page's
  // list under the new one.
  useEffect(() => {
    if (listKeyRef.current !== identityKey) return;
    rememberBrowseList(identityKey, currentList);
  }, [currentList, identityKey]);

  useEffect(() => {
    if (!ready) return undefined;
    if (!isHome && !effectiveId) return undefined;

    // Filters come from the URL alone. The shared search state can still hold a
    // previous page's (a size picked on /search); every filter key is passed,
    // undefined where the URL has none, because setSearchParams merges and only
    // an undefined key is deleted.
    const urlFilters = filtersFromQuery(urlParamsRef.current as Record<string, never>);
    const filters: Record<string, unknown> = {
      ...Object.fromEntries(FILTER_URL_KEYS.map((k) => [k, undefined])),
      ...urlFilters,
    };
    const base = {
      ...withoutFilters(searchParamsRef.current as Record<string, unknown>),
      ...withoutFilters(urlParamsRef.current as Record<string, unknown>),
      ...filters,
    } as SearchParams;

    const sp: SearchParams = isHome
      ? {
          // Home: clean filters + the home-listings endpoint (excludes
          // Pets/Miscellaneous/Jobs). categoryGroupId 0 == "all / home".
          ...(Object.fromEntries(FILTER_URL_KEYS.map((k) => [k, undefined])) as SearchParams),
          q: '',
          categoryGroupId: 0,
          page: 1,
          isHomePage: true,
          sortBy,
        }
      : isSubcategory
        ? {
            ...base,
            categoryId: currentCategoryId,
            // From the URL alone: the previous page's sort must not ride along
            // in searchParams. Undefined deletes it.
            sortBy,
            page: 1,
            isCategoryGroup: false,
            // Never inherit `isHomePage` from a prior home visit — it would
            // route this category search to the home-listings endpoint (which
            // ignores categoryGroupId), so every category returns the same feed.
            isHomePage: false,
            status: 1,
          }
        : {
            ...base,
            categoryGroupId: currentCategoryGroupId,
            sortBy,
            page: 1,
            isCategoryGroup: true,
            // See above — keep category searches off the home-listings endpoint.
            isHomePage: false,
            status: 1,
          };

    // Keep the shared search state in sync with the URL so nav highlighting,
    // "load more", and search-bar all read the correct category/query.
    setSearchParams(sp);
    // The GROUP, also on a subcategory page: this drives the nav highlight and
    // the filter popup's category select, which list groups only. It was
    // `effectiveId`, which on a subcategory page is the subcategory -- matched
    // by nothing, so the filter read "All". It only looked right while the page
    // misread its own URL as the parent group (fixed 2026-10-02).
    setCurrentCategoryGroupId(isHome ? 0 : (currentCategoryGroupId ?? effectiveId));

    const categoryKey = `${isHome ? 'home' : isSubcategory ? 'c' : 'g'}:${effectiveId}`;

    // Back to a page this tab already showed: put back the list the reader
    // left, as it was, and fetch nothing. Refetching emptied the screen for
    // seconds and, with per-load shuffling, brought back different listings.
    // The page count and scroll come back through useScrollRestoration.
    if (isBackNavigation()) {
      const kept = recallBrowseList<Product>(identityKey);
      if (kept) {
        // A fetch still in flight from before (another page's, or this one's
        // from an earlier visit) must not land over what Back put back.
        fetchSeqRef.current += 1;
        listKeyRef.current = identityKey;
        shownCategoryRef.current = categoryKey;
        if (isHome) homeFetchedSortRef.current = sortBy ?? '';
        replaceCurrentList([...kept], sp as Parameters<typeof replaceCurrentList>[1]);
        return undefined;
      }
    }

    // The only cases the server list can't satisfy:
    //  - mobile / no-SSR: there is never a server list (initialList empty), so
    //    every category/home change must fetch client-side.
    //  - in-category text search: the server list is the unfiltered category.
    //  - a picked sort: the server list is ISR-cached in Best match order.
    //  - filters (filter drawer): the server list is ISR-cached unfiltered.
    const hasServerList = initialListRef.current.length > 0;
    const needsClientFetch = (!isHome && !!q) || !!sortBy || !hasServerList || (!isHome && hasActiveFilters(urlFilters));

    // The back/forward guard only matters when there's a server list to protect
    // from being clobbered during restoration (web SSR). On mobile / no-SSR the
    // client fetch is the ONLY populator, so it must run on every category
    // change — including taps the Capacitor webview misreports as back
    // navigation (the bug where the header switched but the listings didn't).
    if (hasServerList && isBackNavigation()) return undefined;

    // Home fetches its grid once per sort: a switch back to grid after showcase
    // reuses the list, a new sort does not.
    if (isHome && (homeShowsShowcase || homeFetchedSortRef.current === (sortBy ?? ''))) return undefined;

    // A home list already fetched for another sort must be replaced even when a
    // server list exists: going back to Best match is a fetch too.
    if (needsClientFetch || (isHome && homeFetchedSortRef.current !== null)) {
      if (isHome) homeFetchedSortRef.current = sortBy ?? '';
      // Arriving at a new category, the list still holds the last page's tiles
      // (there is no server list to swap in with the route), so they would sit
      // under this category's heading until the fetch lands. Emptied, the grid
      // shows its skeleton instead. Not on Back: that list is this page's own,
      // and keeping it holds the scroll position while page 1 is refetched.
      if (shownCategoryRef.current !== categoryKey && !isBackNavigation()) {
        replaceCurrentList([] as Product[]);
      }
      shownCategoryRef.current = categoryKey;
      setPage(1);
      setDone(false);
      // The in-feed ads are rendered BY the listings, so this fetch is what
      // produces them — and the layout's banner and sidebar have already
      // mounted at hydration. Without a hold those are two ad-insert batches,
      // and every batch runs a widget auction that re-requests every slot on
      // the page: the reader watches the banner get fetched twice and repaint.
      // Holding across the fetch puts all of them in one commit, so the page
      // runs one auction. Measured on prod: the home page split into two
      // auctions 89ms apart, before either slot had rendered — which is exactly
      // the window ./ad-refresh-guard cannot cover. See ../ad-insert-batch.
      const releaseAdHold = acquireAdInsertHold();
      // Two fetches can be in flight (a sort picked while the last one loads),
      // and the slower one must not paint over the newer one.
      const seq = ++fetchSeqRef.current;
      const fetchedFor = identityKey;
      const replaceIfLatest = (list: Record<string, unknown>[], params?: Record<string, unknown>) => {
        if (seq !== fetchSeqRef.current) return;
        listKeyRef.current = fetchedFor;
        replaceCurrentList(list as unknown as Product[], params as Parameters<typeof replaceCurrentList>[1]);
      };
      void getFreshList(sp, replaceIfLatest)
        .finally(releaseAdHold);
      // Dropped again on unmount or the next navigation, in case the fetch
      // outlives this page. Releasing twice is a no-op.
      return releaseAdHold;
    }
    // else: forward web navigation — ProductProvider's SSR-sync effect replaces
    // currentList from the fresh server props. Single writer, no double fetch.
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identityKey]);
};

export default useBrowseListings;
